import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { CheckCircle, Clock, Loader2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const orderType = searchParams.get('type');

  const [status, setStatus] = useState('loading');
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (orderType === 'collection' && orderId) {
      fetchOrder(orderId);
    } else if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    } else {
      setStatus('error');
    }
  }, []);

  const fetchOrder = async (id) => {
    try {
      const res = await axios.get(`${API}/orders/${id}`);
      setOrder(res.data);
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  const pollPaymentStatus = async (sid, attempts) => {
    const maxAttempts = 8;
    const pollInterval = 2500;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await axios.get(`${API}/orders/payment-status/${sid}`);
      if (res.data.payment_status === 'paid') {
        setStatus('paid');
        // Fetch the order (via the payment transaction → order_id) so we can
        // show the correct message for collection vs delivery.
        try {
          if (res.data.order_id) {
            const orderRes = await axios.get(`${API}/orders/${res.data.order_id}`);
            setOrder(orderRes.data);
          }
        } catch { /* fall back to generic paid message */ }
        return;
      } else if (res.data.status === 'expired') {
        setStatus('expired');
        return;
      }
      setStatus('processing');
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch {
      setStatus('processing');
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    }
  };

  const renderContent = () => {
    if (status === 'loading' || status === 'processing') {
      return (
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-[#C67C4E]/10 flex items-center justify-center mx-auto mb-6">
            <Loader2 size={40} className="text-[#C67C4E] animate-spin" />
          </div>
          <h1 className="font-serif text-3xl font-bold text-[#1B4332] mb-3">Processing Payment</h1>
          <p className="text-[#636E72] text-lg">Please wait while we confirm your payment...</p>
        </div>
      );
    }

    if (status === 'paid') {
      // 3-way branch:
      //   order.order_type === 'delivery' → deliver-to block
      //   order.order_type === 'collection' → come-collect block
      //   order not loaded / missing → SAFE fallback: generic confirmation with contact info,
      //     never assume it's a collection order (would repeat the bug for delivery customers).
      const knownType = order?.order_type;
      const isDelivery = knownType === 'delivery';
      const isCollection = knownType === 'collection';
      return (
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-green-600" />
          </div>
          <h1 data-testid="payment-success-heading" className="font-serif text-3xl sm:text-4xl font-bold text-[#1B4332] mb-3">Payment Successful!</h1>
          <p className="text-[#636E72] text-lg mb-8">Your order has been confirmed and paid. We&apos;ll start preparing it right away.</p>
          <div
            data-testid={isDelivery ? 'delivery-success-info' : (isCollection ? 'collection-success-info' : 'generic-success-info')}
            className="bg-white rounded-2xl p-6 border border-[#1B4332]/10 shadow-sm mb-8 max-w-md mx-auto"
          >
            <p className="text-sm text-[#636E72] mb-1">What&apos;s next?</p>
            {isDelivery && (
              <>
                <p className="text-[#1B4332] font-medium">We&apos;ll deliver to:</p>
                <p className="font-serif text-lg text-[#C67C4E] font-semibold mt-2">
                  {order?.delivery_address || 'Your delivery address'}
                </p>
                {order?.delivery_postcode && (
                  <p className="text-[#636E72]">{order.delivery_postcode}</p>
                )}
                <p className="text-[#636E72] text-sm mt-3">Our driver will bring it to you shortly. Please keep an eye on your phone.</p>
              </>
            )}
            {isCollection && (
              <>
                <p className="text-[#1B4332] font-medium">Come collect your order at:</p>
                <p className="font-serif text-xl text-[#C67C4E] font-semibold mt-2">La Pasteria Pizza Bar</p>
                <p className="text-[#636E72]">High Street North, Dunstable</p>
              </>
            )}
            {!knownType && (
              <>
                <p className="text-[#1B4332] font-medium">We&apos;re preparing your order.</p>
                <p className="text-[#636E72] text-sm mt-2">
                  Please check the confirmation email for details, or call us on{' '}
                  <a href="tel:01582758636" className="text-[#C67C4E] underline">01582 758636</a>{' '}
                  if you need anything.
                </p>
              </>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/order">
              <Button className="bg-[#1B4332] hover:bg-[#2D6A4F]">Order Again</Button>
            </Link>
            <Link to="/">
              <Button variant="outline">Back to Home</Button>
            </Link>
          </div>
        </div>
      );
    }

    if (status === 'success' && order) {
      return (
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-green-600" />
          </div>
          <h1 data-testid="collection-success-heading" className="font-serif text-3xl sm:text-4xl font-bold text-[#1B4332] mb-3">Order Placed!</h1>
          <p className="text-[#636E72] text-lg mb-8">Your collection order has been submitted. Pay when you pick up.</p>
          <div className="bg-white rounded-2xl p-6 border border-[#1B4332]/10 shadow-sm mb-6 max-w-md mx-auto text-left">
            <h3 className="font-semibold text-[#1B4332] mb-3">Order Summary</h3>
            {order.items?.map((item, i) => (
              <div key={i} className="flex justify-between py-1.5 border-b border-gray-100 last:border-0 text-sm">
                <span>{item.quantity}x {item.name}</span>
                <span className="font-medium">{"\u00A3"}{item.line_total?.toFixed(2)}</span>
              </div>
            ))}
            <div className="flex justify-between mt-3 pt-3 border-t-2 border-[#1B4332]/20">
              <span className="font-bold text-[#1B4332]">Total</span>
              <span className="font-bold text-[#C67C4E]">{"\u00A3"}{order.total?.toFixed(2)}</span>
            </div>
          </div>
          <div className="bg-[#F5F0E8] rounded-2xl p-6 max-w-md mx-auto mb-8">
            <Clock size={24} className="text-[#C67C4E] mx-auto mb-2" />
            <p className="text-[#1B4332] font-medium">Collect from:</p>
            <p className="font-serif text-lg text-[#C67C4E] font-semibold mt-1">La Pasteria Pizza Bar</p>
            <p className="text-[#636E72] text-sm">High Street North, Dunstable</p>
            <p className="text-[#636E72] text-sm mt-1">Payment: In store on collection</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/order">
              <Button className="bg-[#1B4332] hover:bg-[#2D6A4F]">Order Again</Button>
            </Link>
            <Link to="/">
              <Button variant="outline">Back to Home</Button>
            </Link>
          </div>
        </div>
      );
    }

    // Error / timeout / expired
    return (
      <div className="text-center">
        <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
          <XCircle size={40} className="text-red-500" />
        </div>
        <h1 className="font-serif text-3xl font-bold text-[#1B4332] mb-3">
          {status === 'timeout' ? 'Payment Status Unknown' : status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
        </h1>
        <p className="text-[#636E72] text-lg mb-8">
          {status === 'timeout'
            ? 'We couldn\'t confirm your payment. Please contact us if you were charged.'
            : 'Please try placing your order again.'}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/order">
            <Button className="bg-[#1B4332] hover:bg-[#2D6A4F]">Try Again</Button>
          </Link>
          <Link to="/">
            <Button variant="outline">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <Header />
      <section className="pt-32 pb-24 px-4">
        <div className="max-w-3xl mx-auto">
          {renderContent()}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default OrderSuccessPage;
